<template>
  <div class="flex-child">
    <div ref="chartContainer" class="vue-chart">
      <el-skeleton :loading="soldChartDataStatusInjected === AsyncDataStatus.PENDING" animated>
        <template #template>
          <el-skeleton-item style="height: 315px" />
        </template>
        <template #default>
          <apexchart
            v-if="soldChartDataStatusInjected === AsyncDataStatus.SUCCESS"
            width="100%"
            height="300"
            type="area"
            :options="chartOptions"
            :series="seriesList"
          />
        </template>
      </el-skeleton>
    </div>

    <div class="flex-text">
      <p
        v-if="currentToken === Tokens.pearl"
        v-html="$t('dashboardRetrievedTotal', { label: formatters.label.call(currentTokenInfo?.name) })"
      ></p>
      <p
        v-else
        v-html="$t('dashboardRetrievedOnIslandBvR', { label: formatters.label.call(currentTokenInfo?.name) })"
      ></p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ChartDataFormatterProps } from '~/types/common';
import { Tokens } from '~/utils/constants';
import type { ChartDataModel, ChartLabelsModel, ChartSeriesModel, ITokenModel } from '~/types/apiService';
import { AsyncDataStatus } from '~/utils/constants/asyncData';
import apexChartLocales from '~/utils/constants/apexChartLocales';
import type { ApexOptions } from 'apexcharts';
import { Formatters } from '~/utils/formatters/formatters';
import type { AsyncDataRequestStatus } from '#app';

const store = useMainStore();
const { t, locale } = useI18n();
const chartId = 'dex-chart-seabed-retrieved';
const chartContainer = ref<HTMLElement | null>(null);

defineProps<ChartDataFormatterProps>();

const { data: currentTokenInfo } = useNuxtData<ITokenModel>('dashboardCurrentTokenInfo');
const { data: soldData } = useNuxtData<ChartDataModel>('statistics/sold');

const soldChartDataStatusInjected = inject<Ref<AsyncDataRequestStatus> | AsyncDataStatus>(
  'soldChartDataStatus',
  AsyncDataStatus.IDLE
);

const currentToken = computed(() => store.currentToken);

const seabedRetrievedSeries = computed(() => {
  const filteredSeries = soldData.value?.series.filter(
    (serie: ChartSeriesModel) => serie.name.includes('Seabed') || serie.name.includes('RetrievedOnIsland')
  );

  return {
    dates: soldData.value?.dates,
    series: filteredSeries
  } as ChartDataModel;
});

const labels = computed<ChartLabelsModel>(() => {
  const categories = soldData.value?.dates || [];

  return {
    type: 'datetime',
    categories,
    tickAmount: 10,
    labels: {
      rotate: 0
    }
  };
});

const seriesList = computed(() => {
  if (!seabedRetrievedSeries.value || !seabedRetrievedSeries.value.series) return [];

  return seabedRetrievedSeries.value.series.map((serie: ChartSeriesModel) => {
    return {
      ...serie,
      name: t(serie.name)
    };
  });
});

const chartOptions = computed(() => ({
  theme: {
    mode: 'dark'
  },
  grid: {
    borderColor: '#34414A'
  },
  chart: {
    id: chartId,
    class: 'vuechart-example',
    background: '#00000000',
    toolbar: {
      show: false
    },
    zoom: {
      enabled: false
    },
    locales: apexChartLocales,
    defaultLocale: locale.value
  },
  title: {
    text:
      store.currentToken === Tokens.pearl
        ? `${t('dashboardRetrievedFromSeabed')}`
        : `${t('dashboardRetrievedOnIslandLabel')}`,
    align: 'center',
    style: {
      fontSize: '24px'
    }
  },
  stroke: {
    curve: 'smooth'
  },
  responsive: [
    {
      breakpoint: 600,
      options: {
        xaxis: {
          labels: {
            rotate: -45
          }
        }
      }
    }
  ],
  dataLabels: {
    enabled: false
  },
  yaxis: {
    forceNiceScale: true,
    decimalsInFloat: 0,
    labels: {
      formatter: function (val: number) {
        return Formatters.number.renderString((f) => f.formatNumber(val, 5, 10, true));
      }
    }
  },
  colors: ['#1EBFFD'],
  xaxis: labels.value,
  tooltip: {
    x: {
      format: 'dd/MM/yy'
    }
  }
}));

useApexChartUpdater({
  chartId,
  chartContainer,
  chartOptions: chartOptions as ComputedRef<ApexOptions>,
  seriesList,
  labels,
  extraDeps: [currentToken, locale]
});
</script>

<style scoped lang="scss">
.vue-chart {
  min-height: 340px;
}
</style>
